import { AxiosInstance, BearerTokenAuthProvider, TeamsUserCredential, createApiClient } from "@microsoft/teamsfx";
import axios, { AxiosError } from "axios";
import { PropsWithChildren, createContext, useContext, useEffect, useState } from "react";
import { useTeams } from "@microsoft/teamsfx-react";
import { ISignup } from "@interfaces/signup.interfaces";
import { ITenant } from "@interfaces/tenant.interfaces";
import { differenceInSeconds, parseISO } from "date-fns";
import { ILocaleStrings } from "loc/locale.interfaces";
import { useL10n } from "./l10n-context";
import { IAppConfig, IPrincipal } from "@interfaces/common.interfaces";
import { PPAPermission } from "lib/permissions";
import { IPermissionLevel } from "@interfaces/settings/permissions.interfaces";

export interface IPPAContext {
    reloadSignup: () => Promise<ISignup | null>;
    ssoError: string;
    initialisingError: string;
    initialising: boolean;
    disabled: boolean;
    licenceExpired: boolean;
    hasAccess: boolean;
    isSignupComplete: boolean;
    appInstanceApiUrl: string;
    licenceExpiry: string;
    getApiClient: () => AxiosInstance;
    getSignupClient: () => AxiosInstance;
    config: IAppConfig;
    currentUser: IPrincipal | undefined;
    permissionLevel: Array<boolean>;
    hasPermission: (permission: PPAPermission | Array<PPAPermission>, requireAll?: boolean) => boolean;
}

export interface IPPAProviderProps {
    teamsUserCredential?: TeamsUserCredential;
    setInitialising: (initialising: boolean) => void;
    config: IAppConfig;
}

export const PPAContext = createContext<Partial<IPPAContext>>({});

//
export function usePPA(): IPPAContext {
    const ctx = useContext(PPAContext);

    if (ctx === undefined) {
        // This should only really be a developer error if this hook is not used inside the provider.
        throw new Error("usePPA can only be used in a PPAProvider tree");
    }

    return ctx as IPPAContext;
}

export default function PPAProvider(props: PropsWithChildren<IPPAProviderProps>) {
    const [{ context }] = useTeams();
    const { localeCode } = useL10n<ILocaleStrings>();
    const [signup, setSignup] = useState<ISignup | null>(null);
    const [tenant, setTenant] = useState<ITenant | null>(null);
    const [permissionLevel, setPermissionLevel] = useState<Array<boolean>>([]);
    const [reloadSignup, setReloadSignup] = useState<string>();
    const [ssoError, setSsoError] = useState<string>("");
    const [initialisingError, setInitialisingError] = useState<string>("");
    const [isInitialising, setIsInitialising] = useState<boolean>(true);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const [isLicenceExpired, setIsLicenceExpired] = useState<boolean>(false);
    const [hasAccess, setHasAccess] = useState<boolean>(false);
    const [isSignupComplete, setIsSignupComplete] = useState<boolean>(false);
    const [appInstanceApiUrl, setAppInstanceApiUrl] = useState<string>("");
    const [licenceExpiry, setLicenceExpiry] = useState<string>("");
    const [currentUser, setCurrentUser] = useState<IPrincipal | undefined>();

    const setInitialising = (initialising: boolean) => {
        setIsInitialising(initialising);
        props.setInitialising(initialising);
    }

    // Create the api client for each of the api calls.
    const getAxiosClient = (apiUrl: string): AxiosInstance => {
        //
        const apiClient = createApiClient(
            apiUrl,
            new BearerTokenAuthProvider(async () => {
                try {
                    const accessToken = await props.teamsUserCredential?.getToken("");
                    setSsoError("");
                    return accessToken ? accessToken.token : "";
                }
                catch (error: unknown) {
                    console.log(`PPAProvider -> getApiClient ->`, error);
                    const typedError = error as any;

                    if (typedError.message) {
                        console.log(`error.message ->`, typedError.message);
                        setSsoError(typedError.message);
                    }
                    else {
                        setSsoError(JSON.stringify(error));
                    }
                    return "";
                }
            })
        );
        //
        apiClient.defaults.headers.common["Accept-Language"] = localeCode;

        return apiClient;
    };

    const getSignupClient = (): AxiosInstance => {
        return getAxiosClient(props.config.apiUrl || "");
    }

    const getApiClient = (): AxiosInstance => {
        return getAxiosClient(signup?.appInstance.apiUrl || "");
    }

    const getSignup = async (): Promise<ISignup | null> => {
        try {
            console.log(`PPAProvider -> getSignup`);
            const response = await getSignupClient().get<ISignup>(`/api/signups/${context?.user?.tenant?.id}`);
            console.log(`PPAProvider -> getSignup ->`, response);
            setSignup(response.data);
            return response.data;
        }
        catch (ex: unknown | AxiosError<string>) {
            console.log(`PPAProvider -> getSignup -> error ->`, ex);
            if (axios.isAxiosError(ex)) {
                console.log(`Index -> getSignup error -> Axios Error`, ex.code, ex.response?.status);
                if (ex.response?.status === 404) {
                    console.log(`Index -> getSignup error -> 404`);

                }
                else {
                    // Set the initialising error.
                    setInitialisingError(`${ex.code} - ${ex.message}`);
                    //setInitialising(false);
                }
            }

            setInitialising(false);
            return null;
        }
    }

    const hasPermission = (permission: PPAPermission | Array<PPAPermission>, requireAll?: boolean): boolean => {
        //console.log(`hasPermission ->`, permissionLevel, permission);
        if (permissionLevel?.length <= 0)
            return false;
        if (typeof permission === 'number')
            return permissionLevel[permission];
        if (Array.isArray(permission)) {
            if (requireAll)
                return (permission as Array<number>)
                    .map(p => permissionLevel[p])
                    .find(p => !p) === undefined ? true : false;
            else
                return (permission as Array<number>)
                    .find(p => permissionLevel[p]) !== undefined ? true : false;
        }
        return false;
    }

    useEffect(() => {
        if (props.teamsUserCredential && context?.user?.tenant) {
            try {
                console.log(`PPAProvider -> useEffect -> context.app -> locale ->`, context.app?.locale);
                getSignup();
            }
            catch (ex: unknown | AxiosError<string>) {
                console.log(`PPAProvider -> useEffect -> getSignup -> error ->`, ex);
            }
        }
    }, [props.teamsUserCredential, context?.user?.tenant, reloadSignup]);

    //context?.user?.tenant

    useEffect(() => {
        if (props.teamsUserCredential && context?.user) {
            try {
                //console.log(`PPAProvider -> useEffect -> context.user ->`, context.user);
                props.teamsUserCredential.getUserInfo().then((user) => {
                    //console.log(`PPAProvider -> useEffect -> context.user -> getUserInfo ->`, user);
                    setCurrentUser({
                        objectId: user.objectId,
                        upn: user.preferredUserName,
                        displayName: user.displayName,
                        email: ""
                    });
                });
            }
            catch (ex: unknown | AxiosError<string>) {
                console.log(`PPAProvider -> useEffect -> context.user -> error ->`, ex);
            }
        }
    }, [props.teamsUserCredential, context?.user]);

    useEffect(() => {
        if (signup) {
            const getTenant = async () => {
                try {
                    //setInitialising(true);
                    console.log(`PPAProvider -> useEffect[signup] -> getTenant`);
                    const response = await getApiClient().get<ITenant>(`/api/tenants/${signup.m365TenantId}`);
                    console.log(`PPAProvider -> useEffect[signup] -> getTenant ->`, response);
                    setTenant(response.data);
                    //setInitialising(false);
                }
                catch (ex: unknown | AxiosError<string>) {
                    console.log(`PPAProvider -> useEffect[signup] -> getTenant -> error ->`, ex);

                    // Set the initialising error.
                    if (axios.isAxiosError(ex)) {
                        const message =
                            ex.response?.data ?
                                typeof ex.response.data === 'string' ?
                                    ex.response.data
                                    :
                                    JSON.stringify(ex.response.data)
                                :
                                ex.message;
                        setInitialisingError(`${ex.code} - ${message}`);
                    }
                    else {
                        setInitialisingError(JSON.stringify(ex));
                    }

                    setInitialising(false);
                }
            }
            if (signup.enabled) {
                getTenant();
            }
            else {
                console.log(`PPAContext -> usEffect[signup] -> signup disabled ->`, signup);
                setInitialising(false);
            }
        }
    }, [signup]);

    useEffect(() => {
        console.log(`PPAContext -> usEffect[signup, tenant] ->`, signup, tenant);
        if (signup) {
            setIsDisabled(!signup.enabled);
            const expiry = parseISO(signup.licenceAbsoluteExpiry);
            setIsLicenceExpired(differenceInSeconds(expiry, new Date()) < 0);
            setLicenceExpiry(signup.licenceAbsoluteExpiry);
            setIsSignupComplete(signup.isSignupComplete);
            if (signup.appInstance) {
                setAppInstanceApiUrl(signup.appInstance.apiUrl);
            }
        }
        else {

        }
    }, [signup, tenant]);

    useEffect(() => {
        if (tenant) {
            // TODO: At some point, this could be changed to be getUserProfile
            // And it can include more details about the user like their preferences inside Audit.
            const getPermissionLevel = async () => {
                try {
                    // Ensure the initialising flag is set here as if this was a signup, it might have been set to false.
                    setInitialising(true);
                    console.log(`PPAProvider -> useEffect[tenant] -> getPermissionLevel`);
                    const response = await getApiClient().get<IPermissionLevel>(`/api/settings/permissions/permission-level`);
                    console.log(`PPAProvider -> useEffect[tenant] -> getPermissionLevel ->`, response);
                    setPermissionLevel(
                        response.data.permissionLevel
                            .split('')
                            .map(
                                (bit: string) => {
                                    return bit === '1' ? true : false
                                })
                    );
                }
                catch (ex: unknown | AxiosError<string>) {
                    console.log(`PPAProvider -> useEffect[tenant] -> getPermissionLevel -> error ->`, ex);

                    // Set the initialising error.
                    if (axios.isAxiosError(ex)) {
                        const message =
                            ex.response?.data ?
                                typeof ex.response.data === 'string' ?
                                    ex.response.data
                                    :
                                    JSON.stringify(ex.response.data)
                                :
                                ex.message;
                        setInitialisingError(`${ex.code} - ${message}`);
                    }
                    else {
                        setInitialisingError(JSON.stringify(ex));
                    }

                    setInitialising(false);
                }
            }
            getPermissionLevel();
        }
    }, [tenant]);

    useEffect(() => {
        if (permissionLevel.length > 0) {
            setInitialising(false);
        }
    }, [permissionLevel]);

    useEffect(() => {
        if (permissionLevel.length > 0) {
            setInitialising(false);
        }
    }, [permissionLevel]);

    useEffect(() => {
        setHasAccess(!isDisabled && !isLicenceExpired && !initialisingError);
    }, [isDisabled, isLicenceExpired, initialisingError]);

    const tempWrapper: IPPAContext = {
        reloadSignup: getSignup,
        ssoError: ssoError,
        initialisingError: initialisingError,
        initialising: isInitialising,
        disabled: isDisabled,
        licenceExpired: isLicenceExpired,
        hasAccess: hasAccess,
        isSignupComplete: isSignupComplete,
        appInstanceApiUrl: appInstanceApiUrl,
        licenceExpiry: licenceExpiry,
        getApiClient: getApiClient,
        getSignupClient: getSignupClient,
        config: props.config,
        currentUser: currentUser,
        permissionLevel: permissionLevel,
        hasPermission: hasPermission
    };

    return (
        <PPAContext.Provider value={tempWrapper}>{props.children}</PPAContext.Provider>
    )
}